import type { ReactNode } from "react";
import { AnimatedNumber } from "../../../components/AnimatedNumber";
import { Card } from "../../../components/Card";
import { Sparkline } from "../../../components/Sparkline";

type MetricTone = "accent" | "success" | "warning" | "danger" | "neutral";

const toneClass: Record<MetricTone, string> = {
  accent: "text-accent-700",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  neutral: "text-text",
};

const toneBarClass: Record<MetricTone, string> = {
  accent: "bg-accent-300",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  neutral: "bg-border-strong",
};

export function MetricTile({
  label,
  value,
  suffix,
  detail,
  icon,
  tone = "neutral",
  sparkline,
  change,
}: {
  label: string;
  value: number | string;
  suffix?: string;
  detail: string;
  icon?: ReactNode;
  tone?: MetricTone;
  sparkline?: number[];
  change?: string;
}) {
  const numeric = typeof value === "number";
  const sparkTone: "accent" | "success" | "warning" | "danger" =
    tone === "neutral" ? "accent" : tone;

  return (
    <Card
      className={`grid ${sparkline ? "min-h-40" : "min-h-32"} gap-4 overflow-hidden`}
      padding="lg"
      variant="surface"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="cell-label">{label}</p>
          <p
            className={`num mt-2 flex flex-wrap items-baseline gap-2 font-sans text-[2rem] font-semibold leading-none ${toneClass[tone]}`}
          >
            {numeric ? <AnimatedNumber value={value} /> : value}
            {suffix ? <span className="text-lg">{suffix}</span> : null}
            {change ? (
              <span
                className={`text-xs font-bold ${
                  change.startsWith("-") ? "text-danger" : "text-success"
                }`}
              >
                {change}
              </span>
            ) : null}
          </p>
        </div>
        <span
          aria-hidden="true"
          className={`mt-2 h-2.5 w-2.5 rounded-pill ${toneBarClass[tone]}`}
        />
        {icon ? (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-border bg-surface-raised text-text-muted">
            {icon}
          </span>
        ) : null}
      </div>
      <div className="mt-auto flex items-end justify-between gap-3 border-t border-border pt-3">
        <p className="text-xs font-semibold leading-5 text-text-subtle">{detail}</p>
        {sparkline ? <Sparkline points={sparkline} tone={sparkTone} /> : null}
      </div>
    </Card>
  );
}
